import React, { useEffect, useRef } from 'react';
import { Box, Skeleton } from '@mui/material';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import FlightTakeoffOutlinedIcon from '@mui/icons-material/FlightTakeoffOutlined';
import ChurchOutlinedIcon from '@mui/icons-material/ChurchOutlined';
import ParkOutlinedIcon from '@mui/icons-material/ParkOutlined';
import ForestOutlinedIcon from '@mui/icons-material/ForestOutlined';
import LocalPoliceOutlinedIcon from '@mui/icons-material/LocalPoliceOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import LocalLibraryOutlinedIcon from '@mui/icons-material/LocalLibraryOutlined';
import MuseumOutlinedIcon from '@mui/icons-material/MuseumOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import SportsSoccerOutlinedIcon from '@mui/icons-material/SportsSoccerOutlined';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';
import LocalGroceryStoreOutlinedIcon from '@mui/icons-material/LocalGroceryStoreOutlined';
import LocalMallOutlinedIcon from '@mui/icons-material/LocalMallOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReferenceLayerRow from '../ReferenceLayersPanel/ReferenceLayerRow';
import { getReferencePlaceKindColor } from '../../utils/referencePlaceIcons';
import { referencePlaceCategoryLabel } from '../../data/referencePlaceKinds';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../../hooks/useScrollEdgeFade';
import {
  mapControlsPanelBackground,
  mapControlsPanelMaxWidth,
  mapControlsPanelRight,
  mapControlsReferencePanelTop,
  mapControlsReferencePlacesPanelHeight,
} from '../MapControls/MapControls.styles';
import MapToolPanelHeader from '../MapControls/MapToolPanelHeader';

const KIND_ICONS = {
  school: SchoolOutlinedIcon,
  hospital: LocalHospitalOutlinedIcon,
  airport: FlightTakeoffOutlinedIcon,
  church: ChurchOutlinedIcon,
  national_park: ForestOutlinedIcon,
  park: ParkOutlinedIcon,
  police: LocalPoliceOutlinedIcon,
  prison: GavelOutlinedIcon,
  library: LocalLibraryOutlinedIcon,
  museum: MuseumOutlinedIcon,
  monument: AccountBalanceOutlinedIcon,
  cultural_org: PaletteOutlinedIcon,
  sports: SportsSoccerOutlinedIcon,
  gym: FitnessCenterOutlinedIcon,
  supermarket: LocalGroceryStoreOutlinedIcon,
  mall: LocalMallOutlinedIcon,
  landfill: DeleteOutlineIcon,
};

const SKELETON_SX = { bgcolor: 'rgba(255,255,255,0.08)' };
const SKELETON_ROW_COUNT = 7;

function ReferencePlacesPanelSkeleton() {
  return (
    <Box aria-hidden sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '8px',
            borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.07)',
            backgroundColor: 'rgba(255,255,255,0.02)',
          }}
        >
          <Skeleton variant="rounded" width={40} height={40} sx={{ ...SKELETON_SX, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton variant="rounded" width={`${62 - (index % 3) * 8}%`} height={14} sx={SKELETON_SX} />
            <Skeleton
              variant="rounded"
              width={`${38 - (index % 2) * 6}%`}
              height={10}
              sx={{ ...SKELETON_SX, mt: '6px' }}
            />
          </Box>
          <Skeleton variant="rounded" width={32} height={18} sx={{ ...SKELETON_SX, borderRadius: '9px', flexShrink: 0 }} />
        </Box>
      ))}
    </Box>
  );
}

const noop = () => {};

/**
 * Switcher de lugares de referencia. Independiente de "Capas de referencia"
 * (inundación / pobreza / zonificación).
 */
const ReferencePlacesPanel = ({
  kinds = [],
  enabledKinds = [],
  onToggleKind = noop,
  locked = false,
  loading = false,
  viewportHint = '',
  onClose = noop,
  topOffset = 0,
}) => {
  const enabledSet = new Set(enabledKinds);
  const panelRef = useRef(null);
  const {
    scrollRef,
    fadeTop,
    fadeBottom,
    refreshScrollEdges,
  } = useScrollEdgeFade();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    refreshScrollEdges();
  }, [kinds, enabledKinds, loading, viewportHint, refreshScrollEdges]);

  const setListNode = (node) => {
    scrollRef(node);
  };

  return (
    <div
      ref={panelRef}
      style={{
        position: 'absolute',
        right: mapControlsPanelRight,
        top: mapControlsReferencePanelTop(topOffset),
        zIndex: 1100,
        backgroundColor: mapControlsPanelBackground,
        borderRadius: '12px',
        border: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        display: 'flex',
        flexDirection: 'column',
        width: '360px',
        maxWidth: mapControlsPanelMaxWidth,
        height: mapControlsReferencePlacesPanelHeight(topOffset),
        boxSizing: 'border-box',
        overflow: 'hidden',
        colorScheme: 'dark',
        animation: 'refLayerPanelIn 0.15s ease-out',
      }}
    >
      <MapToolPanelHeader
        icon={PlaceOutlinedIcon}
        title="Puntos de interés"
        onClose={onClose}
        padded
        backgroundColor={mapControlsPanelBackground}
      />

      <Box
        ref={setListNode}
        aria-busy={loading || undefined}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          padding: '10px 12px 12px',
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          colorScheme: 'dark',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255,255,255,0.22) transparent',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255,255,255,0.10)',
            borderRadius: 8,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(255,255,255,0.18)',
          },
          '&::-webkit-scrollbar-corner': { background: 'transparent' },
          ...getScrollEdgeFadeSx(fadeTop, fadeBottom),
        }}
      >
        {loading ? (
          <ReferencePlacesPanelSkeleton />
        ) : kinds.map((entry) => {
          const Icon = KIND_ICONS[entry.kind] || PlaceOutlinedIcon;
          const enabled = enabledSet.has(entry.kind);
          const categoryLabel = referencePlaceCategoryLabel(entry.category);
          return (
            <ReferenceLayerRow
              key={entry.kind}
              icon={Icon}
              iconColor={getReferencePlaceKindColor(entry.kind)}
              title={entry.label}
              sublabel={viewportHint && enabled && !locked ? viewportHint : categoryLabel}
              enabled={enabled && !locked}
              onToggle={() => onToggleKind(entry.kind)}
              disabled={locked}
            />
          );
        })}
      </Box>
    </div>
  );
};

export default ReferencePlacesPanel;
